/**
 * @module components/YAxisSetting
 * @description Y 轴配置卡片入口（ConfigCard 容器，子配置项独立文件维护）
 *
 * 状态管理模式：子组件各自读写 Context；双 Y 轴开关为功能簇（读 useSettingState，写 usePatchDraft）
 * 显隐模式：整卡由 Panel Config A visible 控制；折线双 Y 轴开关在 index 层，用开关状态控制单轴子项
 *
 * 子模块：
 *   - DualYAxisSwitch    双 Y 轴开关（状态在 index）
 *   - DualYAxisSetting   主/次轴配置（开关开启时展示）
 *   - YAxisName          Y 轴名称（开关关闭时展示）
 *   - YAxisRangeSwitch   显示范围（开关关闭时展示）
 *   - YAxisTopN            条形图 Y 轴 TopN（单指标）
 *   - YAxisSortBy          条形图 Y 轴排序（复用 X 轴排序逻辑与上传字段）
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import XAxisSortBy from '../XAxisSetting/XAxisSortBy';
import YAxisName from './YAxisName';
import YAxisRangeSwitch from './YAxisRangeSwitch';
import YAxisTopN from './YAxisTopN';
import DualYAxisSwitch from './DualYAxisSwitch';
import DualYAxisSetting from './DualYAxisSetting';
import { createEmptyLineAxis } from './dualYAxisConstants';
import { usePatchDraft, useSettingState } from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import { toChartTypeEnum } from '../../utils/toChartTypeEnum';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import xAxis from '@/pages/datalab/assets/svg/x-Axis.svg';
import './DualYAxisSetting/index.scss';
import './index.scss';

interface IYAxisSettingProps {
  /** 子项显隐表 — 由 Panel 层根据 Config A + QB 计算后传入 */
  visibleItems?: Record<string, boolean>;
}

/**
 * YAxisSetting — Y 轴配置分类组件
 */

const YAxisSetting: React.FC<IYAxisSettingProps> = ({ visibleItems }) => {
  const { formatMessage } = useIntl();
  const patchDraft = usePatchDraft();
  const chartTypeStr = useQuerySelector(s => s.chartType as string);
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const chartType = useMemo(
    () => toChartTypeEnum(chartTypeStr),
    [chartTypeStr],
  );
  const { value: dualYAxisEnabled } = useSettingState('dualYAxisEnabled');

  const metricCount = columnMetrics?.length || 0;
  const dualSwitchDisabled = metricCount <= 1;

  useEffect(() => {
    if (dualSwitchDisabled && dualYAxisEnabled) {
      patchDraft({
        dualYAxisEnabled: false,
        primaryYAxis: createEmptyLineAxis(chartType),
        secondYAxis: createEmptyLineAxis(chartType),
      });
    }
  }, [chartType, dualSwitchDisabled, dualYAxisEnabled, patchDraft]);

  const handleDualYAxisSwitchChange = useCallback(
    (nextEnabled: boolean): void => {
      if (nextEnabled) {
        patchDraft({ dualYAxisEnabled: true });
        return;
      }
      patchDraft({
        dualYAxisEnabled: false,
        primaryYAxis: createEmptyLineAxis(chartType),
        secondYAxis: createEmptyLineAxis(chartType),
      });
    },
    [chartType, patchDraft],
  );

  const showDualYAxisBlock = visibleItems?.DualYAxis === true;
  const showAxisName =
    visibleItems?.YAxisName === true &&
    (!showDualYAxisBlock || !dualYAxisEnabled);
  const showRangeSwitch =
    visibleItems?.YAxisRange === true &&
    (!showDualYAxisBlock || !dualYAxisEnabled);
  const showTopN = visibleItems?.TopN === true;
  const showSortBy =
    visibleItems?.SortBy === true ||
    visibleItems?.MetricSort === true ||
    visibleItems?.ManualSort === true ||
    visibleItems?.DatasetFieldSort === true;

  return (
    <ConfigCollapseCard
      className="dbl-y-axis-setting"
      title={(<>
        <img src={xAxis} alt="" style={{ marginRight: 5 }} />
        {formatMessage({ id: 'dbl_chart_y_axis' })}
      </>)}
    >
      {showDualYAxisBlock && (
        <div className="dbl-dual-y-axis-setting">
          <DualYAxisSwitch
            value={dualYAxisEnabled}
            disabled={dualSwitchDisabled}
            onChange={handleDualYAxisSwitchChange}
          />
          {dualYAxisEnabled && <DualYAxisSetting />}
        </div>
      )}
      {showAxisName && <YAxisName />}
      {showTopN && <YAxisTopN />}
      {showSortBy && <XAxisSortBy visibleItems={visibleItems} />}
      {showRangeSwitch && <YAxisRangeSwitch />}
    </ConfigCollapseCard>
  );
};

export default React.memo(YAxisSetting);
